import React from "react";
import { formatTime, formatDuration, isToday } from "../utils/date-helpers-consolidated";
import { COLOR_PALETTE, LOCATION_ICONS } from "../utils/constants";
import { EventData } from "./Shared/Types";
import { calculateTotalTimeAway} from "../utils/commonFunctions";

import '../css/employee-portal.css'; 
import '../css/calendar.css'; 

interface EmployeeCardProps {
    date: Date;
    employee: any; // Replace with more specific type if available
    colorIndex: any;

    viewUserProfile: (employee: any) => void; // Replace with more specific type if available
  }

export const EmployeeCard = ({ employee, colorIndex, date, viewUserProfile }:EmployeeCardProps) => {
      const colors = COLOR_PALETTE[colorIndex];
      // Get all sign-ins for the date
      const allSignInsForDate = employee.signIns
        .filter((s:EventData) => {
          const eventDate = s.eventDate;
          return eventDate.getFullYear() === date.getFullYear() && 
             eventDate.getMonth() === date.getMonth() && 
             eventDate.getDate() === date.getDate();
        })
        .sort((a:EventData, b:EventData) => {
          return a.eventDate.getTime() - b.eventDate.getTime();
        });
      
      // Filter out "signed in at main office" events
      const signInsForDate = allSignInsForDate.filter((s:any) => !(s.action === 'in' && s.isMainOffice));
      
      // Find if user is currently out 
      let isCurrentlyOut = false;
      
      // Get the last event to determine current status
      if (allSignInsForDate.length > 0) {
        const lastEvent:EventData = allSignInsForDate[allSignInsForDate.length - 1];
        isCurrentlyOut = lastEvent.eventType === 'out';
      }
      
      // Calculate total time from when signed out of main office to when signed out of visiting office
      const totalTimeOut = calculateTotalTimeAway(allSignInsForDate);
      
      // Group by location for better display
      const locationGroups: Record<string, any[]> = {};
      signInsForDate.forEach((entry: EventData) => {
        if (!locationGroups[entry.location]) {
          locationGroups[entry.location] = [];
        }
        locationGroups[entry.location].push(entry);
      });
      
      return (
        <div 
          className="employee-card-apple"
          onClick={() => viewUserProfile(employee.name)}
        >
          <div className="employee-card-header-apple">
            <div 
              className="employee-initial-apple" 
              style={{ backgroundColor: colors.bg, color: colors.initial }}
            >
              {employee.name.charAt(0)}
            </div>
            
            <div className="employee-info-apple">
              <div className="employee-name-apple">{employee.name}</div>
              <div 
                className={`employee-type-apple ${employee.type.toLowerCase()}`}
              >
                {employee.type}
              </div>
            </div>
            
            <div 
              className={`status-indicator-apple ${isCurrentlyOut ? 'out' : 'in'}`}
            >
              {isCurrentlyOut ? 'OUT' : 'IN'}
            </div>
          </div>
          
          <div className="employee-timeline-apple">
            {Object.entries(locationGroups).map(([location, entries], locIndex) => (
              <div key={locIndex} className="location-group-apple">
                <div className="location-label-apple">
                  <span className="location-icon-apple">
                    {LOCATION_ICONS[location] || '📍'}
                  </span>
                  {location}
                </div>
                
                <div className="time-entries-apple">
                  {entries.map((entry:EventData, entryIndex:number) => (
                    <div 
                      key={entryIndex} 
                      className={`time-entry-apple ${entry.eventType}`}
                    >
                      <span className="time-value-apple">
                        {(() => {
                          if (entry.eventDate) {
                            return <div>{formatTime(entry.eventDate)}</div>;
                          }
                        })()}
                      </span>
                      <span className="action-label-apple">
                        {entry.eventType === 'in' ? 'Signed In' : 'Signed Out'}
                        {entry.isMainOffice ? ' (Main Office)' : ' (Visited Office)'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          {totalTimeOut > 0 && (
            <div className="total-time-apple">
              <span className="time-icon-apple">⏱</span>
              <span className="time-label-apple">Total time away:</span>
              <span className="time-value-apple bold">{formatDuration(totalTimeOut)}</span>
            </div>
          )}
          
          <div className="card-footer-apple">
            <div className="view-profile-button-apple">
              <span>View Profile</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.59 16.59L13.17 12L8.59 7.41L10 6L16 12L10 18L8.59 16.59Z" fill="currentColor"/>
              </svg>
            </div>
          </div>
        </div>
      );
    };


    interface EmployeeCardManagerViewProps {
      employee: {
        id: number,
        name: string,
        type: string,
        teamId: number,
        signIns: EventData[],
      }; // Replace with more specific type if available
      colorIndex: number;
    }

export const EmployeeCardManagerView = ({ employee, colorIndex }:EmployeeCardManagerViewProps) => {
  const { name, type, signIns } = employee;
  const colors = COLOR_PALETTE[colorIndex];
  
  // Get employee status (IN/OUT) based on last event
  const getStatus = () => {
    if (signIns.length === 0) return "in"; // Default to IN if no events
    
    // Sort events by date (most recent first)
    const sortedEvents = [...signIns].sort((a:EventData, b:EventData) =>  a.eventDate.getTime() - b.eventDate.getTime());
    
    // Check last event's action
    const lastEvent = sortedEvents[0];
    return lastEvent.eventType === "in" ? "in" : "out";
  };
  
  const status = getStatus();
  
  // Get initials for avatar
  const getInitials = () => {
    return name.split(' ').map((n:string) => n[0]).join('').toUpperCase();
  };
  
  interface GroupTypes {
    [key: string]: any[];
  }

  // Group events by location
  const groupEventsByLocation = ():GroupTypes => {
    const groups:GroupTypes = {};
    const todayEvents = signIns.filter((event:EventData) => isToday(event.eventDate));
    
    todayEvents.forEach((event:any) => {
      if (!groups[event.location]) {
        groups[event.location] = [];
      }
      groups[event.location].push(event);
    });
    
    // Sort events within each location
    Object.keys(groups).forEach(location => {
      groups[location].sort((a:EventData, b:EventData) => a.eventDate.getTime() - b.eventDate.getTime());
    });
    
    return groups;
  };
  
  const locationGroups = groupEventsByLocation();
  
  // Calculate total time away
  
  
  const totalTimeAway = calculateTotalTimeAway(signIns);
  
  return (
    <div className="employee-card" style={{ 
      display: 'flex', 
      flexDirection: 'column',
      width: '100%',
      height: 'auto',
      overflow: 'visible',
      marginBottom: '16px'
    }}>
      <div className="employee-header" style={{ 
        width: '100%', 
        order: -1,
        marginBottom: '10px'
      }}>
        <div className="employee-info">
          <div 
            className="employee-avatar"
            style={{ backgroundColor: colors.bg, color: colors.text }}
          >
            {getInitials()}
          </div>
          <div className="employee-details">
            <div className="employee-name">{name}</div>
            <div className={`employee-type ${type.toLowerCase()}`}>
              {type}
            </div>
          </div>
        </div>
        
        <div className={`status-indicator ${status}`}>
          {status.toUpperCase()}
        </div>
      </div>
      
      {signIns.length > 0 ? (
        <div className="timeline-container" style={{ 
          width: '100%', 
          marginTop: '10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          {Object.keys(locationGroups).map((location, locationIndex) => (
            <div key={locationIndex} className="location-group" style={{ width: '100%' }}>
              <div className="location-label">
                {LOCATION_ICONS[location] || "📍"} {location}
              </div>
              <div className="time-entries" style={{ width: '100%' }}>
                {locationGroups[location].map((event:EventData, eventIndex:number) => (
                  <div key={eventIndex} className={`time-entry ${event.eventType}`} style={{ 
                    width: '100%',
                    boxSizing: 'border-box'
                  }}>
                    <div className="time-value">{formatTime(event.eventDate)}</div>
                    <div className="time-label">
                      {event.eventType === "in" ? "Signed In" : "Signed Out"}
                      {event.isMainOffice ? " (Main Office)" : " (Visited Office)"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="timeline-container" style={{ width: '100%', marginTop: '10px' }}>
          <div className="no-events">No events recorded today</div>
        </div>
      )}
      
      {totalTimeAway > 0 && (
        <div className="total-time" style={{ 
          width: '100%',
          marginTop: '10px',
          paddingTop: '10px',
          borderTop: '1px solid var(--border-color, #e1e1e1)'
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.99 2C6.47 2 2 6.48 2 12C2 17.52 6.47 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.58 20 4 16.42 4 12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12C20 16.42 16.42 20 12 20ZM12.5 7H11V13L16.25 16.15L17 14.92L12.5 12.25V7Z" fill="currentColor"/>
          </svg>
          Total time away: <span className="time-amount">{formatDuration(totalTimeAway)}</span>
        </div>
      )}
    </div>
  );
};

